import { z } from 'zod';
import { userIdSchema } from './user';
/*
  Security Groups (KIR-3800): named sets of users inside a single organization.
  Policies can be applied per group; a user belongs to at most one group per org.

  Membership on the wire is expressed with **Descope user IDs** (like every other
  organization-member API), even though the backend stores the internal user
  UUID. The backend translates at the handler boundary, so the frontend only ever
  deals with the Descope IDs it already has from the members list.
*/
/*
  securityGroupSchema is the response shape for a single security group.
*/
/*
  Identity of whoever created a security group, for the Security Groups table's
  "Created by" column. `id` is the creator's **Descope** user ID, matching how
  membership is expressed on the wire (see the module note above) — the internal
  user UUID is never exposed.
*/
export const securityGroupCreatorSchema = z.object({
    id: z.string(),
    name: z.string().nullable(),
    email: z.string().nullable(),
});
export const securityGroupSchema = z.object({
    id: z.string(),
    organizationId: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    // Who created the group. `null` for groups created before creator tracking
    // existed, and for groups whose creator has since been removed from the
    // system — the group deliberately outlives its creator.
    createdBy: securityGroupCreatorSchema.nullable().optional(),
    // Soft-delete marker. Only ever non-null on responses that explicitly opted
    // into deleted groups (`includeDeleted`), which today is just the alerts
    // filter: alerts keep naming the group that triggered them, so that filter
    // must be able to offer a deleted group as a value. Every other consumer
    // (Policies scope selector, Security Groups page) omits them entirely.
    deletedAt: z.coerce.date().nullable().optional(),
    // Member count for the org's Policies scope selector (AC #7). Populated by the
    // LIST endpoint; omitted on single-group responses (create/get/update).
    userCount: z.number().int().nonnegative().optional(),
});
/*
  securityGroupListSchema is the response shape for listing an org's groups.
*/
export const securityGroupListSchema = z.array(securityGroupSchema);
/*
  "Which security group am I in" (KIR-4334). Object-wrapped so a 200 body is
  always an object. `group` is null when the caller is in no group — the caller
  then falls back to the org's policies, as enforcement does.
*/
export const mySecurityGroupSchema = z.object({
    group: securityGroupSchema.nullable(),
});
// Shared field validators for create/update inputs. Names are trimmed and length
// capped; uniqueness (per org) is enforced by the DB and surfaced as a 409.
const groupNameSchema = z
    .string()
    .trim()
    .min(1, 'Group name is required')
    .max(100, 'Group name must be 100 characters or fewer');
const groupDescriptionSchema = z
    .string()
    .trim()
    .max(500, 'Description must be 500 characters or fewer')
    .nullish();
/*
  newSecurityGroupSchema validates the create-group request body.
*/
export const newSecurityGroupSchema = z.object({
    name: groupNameSchema,
    description: groupDescriptionSchema,
});
/*
  updateSecurityGroupSchema validates the update-group request body. Both fields
  are optional, but at least one must be provided (an empty patch is rejected).
*/
export const updateSecurityGroupSchema = z
    .object({
    name: groupNameSchema.optional(),
    description: groupDescriptionSchema,
})
    .refine(data => data.name !== undefined || data.description !== undefined, {
    message: 'At least one of name or description must be provided',
});
/*
  securityGroupMemberSchema is the response shape for a group membership row.
  `userId` is the member's Descope user ID (see the module note above).
*/
export const securityGroupMemberSchema = z.object({
    id: z.string(),
    groupId: z.string(),
    userId: z.string(),
    createdAt: z.coerce.date(),
});
/*
  securityGroupMemberListSchema is the response shape for listing a group's
  members.
*/
export const securityGroupMemberListSchema = z.array(securityGroupMemberSchema);
/*
  addSecurityGroupMemberSchema validates the add-member request body. `userId` is
  the member's Descope user ID. Adding a user who already belongs to another
  group in the org moves them (single-group-per-user-per-org invariant).
*/
export const addSecurityGroupMemberSchema = z.object({
    userId: userIdSchema,
});
//# sourceMappingURL=security-groups.js.map